import { useEffect, useState } from "react";
import apiWithRefresh from "../utils/apiWithRefresh";
import useDarkMode from "./useDarkMode";
import {
  Box,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Typography,
  IconButton,
  Stack,
} from "@mui/material";
import { useToast } from "../utils/useToast";
import GlobalToast from "../components/GlobalToast";
import { DataGrid } from "@mui/x-data-grid";
import EditIcon from "@mui/icons-material/Edit";
import DeleteIcon from "@mui/icons-material/Delete";
import AddIcon from "@mui/icons-material/Add";
import { Helmet } from "react-helmet-async";
const AdminLevels = () => {
  useDarkMode();
  const [levels, setLevels] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [form, setForm] = useState({
    id: null,
    name: "",
    focus: "",
    bulletpoints: [""],
  });
  const [modalOpen, setModalOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [deleteId, setDeleteId] = useState(null);
  const [confirmOpen, setConfirmOpen] = useState(false);
  const [toast, showToast] = useToast();

  const fetchLevels = async () => {
    setLoading(true);
    setError(null);
    try {
      const data = await apiWithRefresh.get("levels");
      setLevels(data);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchLevels();
  }, []);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setForm((f) => ({ ...f, [name]: value }));
  };

  const handleBulletpointChange = (idx, value) => {
    setForm((f) => {
      const points = [...f.bulletpoints];
      points[idx] = value;
      return { ...f, bulletpoints: points };
    });
  };

  const handleAddBulletpoint = () => {
    setForm((f) => ({ ...f, bulletpoints: [...f.bulletpoints, ""] }));
  };

  const handleRemoveBulletpoint = (idx) => {
    setForm((f) => {
      const points = [...f.bulletpoints];
      points.splice(idx, 1);
      return { ...f, bulletpoints: points.length ? points : [""] };
    });
  };

  const openAddModal = () => {
    setForm({ id: null, name: "", focus: "", bulletpoints: [""] });
    setEditingId(null);
    setModalOpen(true);
  };

  const openEditModal = async (level) => {
    try {
      const latest = await apiWithRefresh.get(`levels/${level.id}`);
      setForm({
        id: latest.id,
        name: latest.name || "",
        focus: latest.focus || "",
        bulletpoints:
          Array.isArray(latest.bulletpoints) && latest.bulletpoints.length > 0
            ? latest.bulletpoints
            : [""],
      });
      setEditingId(latest.id);
      setModalOpen(true);
    } catch (err) {
      showToast("Failed to fetch level details.", "error");
    }
  };

  const handleModalClose = () => {
    setModalOpen(false);
    setEditingId(null);
    setForm({ id: null, name: "", focus: "", bulletpoints: [""] });
  };

  const handleAddOrUpdate = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) {
      showToast("Name is required.", "warning");
      return;
    }
    try {
      const payload = {
        name: form.name,
        focus: form.focus,
        bulletpoints: form.bulletpoints.filter((p) => p.trim()),
      };
      if (editingId) {
        await apiWithRefresh.put(`levels/${editingId}`, payload);
        showToast("Level updated successfully!", "success");
      } else {
        await apiWithRefresh.post("levels", payload);
        showToast("Level added successfully!", "success");
      }
      setModalOpen(false);
      setEditingId(null);
      setForm({ id: null, name: "", focus: "", bulletpoints: [""] });
      fetchLevels();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  const openDeleteConfirm = (id) => {
    setDeleteId(id);
    setConfirmOpen(true);
  };

  const handleDelete = async () => {
    try {
      await apiWithRefresh.delete(`levels/${deleteId}`);
      showToast("Level deleted successfully!", "success");
      setConfirmOpen(false);
      setDeleteId(null);
      fetchLevels();
    } catch (err) {
      showToast(err.message, "error");
    }
  };

  // DataGrid columns
  const columns = [
    { field: "name", headerName: "Name", flex: 1 },
    { field: "focus", headerName: "Focus", flex: 1 },
    {
      field: "actions",
      headerName: "Actions",
      sortable: false,
      renderCell: (params) => (
        <Stack direction="row" spacing={1}>
          <IconButton
            color="primary"
            onClick={() => openEditModal(params.row)}
            aria-label={`Update level ${params.row.name}`}
          >
            <EditIcon />
          </IconButton>
          <IconButton
            color="error"
            onClick={() => openDeleteConfirm(params.row.id)}
            aria-label={`Delete level ${params.row.name}`}
          >
            <DeleteIcon />
          </IconButton>
        </Stack>
      ),
      flex: 1,
    },
  ];

  return (
    <>
      <Helmet>
        <title>AKAAL KIDS - Admin Panel | Users</title>
      </Helmet>
      <Box
        sx={{
          mt: 4,
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
        }}
      >
        <Typography variant="h4" gutterBottom sx={{ mb: 2 }}>
          Manage Levels
        </Typography>
        {loading && <Typography>Loading...</Typography>}
        {error && (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        {!loading && !error && (
          <Box sx={{ width: "100%", maxWidth: 800 }}>
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={openAddModal}
              sx={{ mb: 2 }}
            ></Button>
            <DataGrid
              autoHeight
              rows={levels.map((l) => ({ ...l, id: l.id }))}
              columns={columns}
              pageSize={10}
              rowsPerPageOptions={[10, 20, 50]}
              disableSelectionOnClick
            />
          </Box>
        )}
        {/* Add/Edit Dialog */}
        <Dialog
          open={modalOpen}
          onClose={handleModalClose}
          maxWidth="sm"
          fullWidth
        >
          <DialogTitle>
            {editingId ? "Edit Level" : "Add New Level"}
          </DialogTitle>
          <form onSubmit={handleAddOrUpdate}>
            <DialogContent>
              <TextField
                label="Name"
                name="name"
                value={form.name}
                onChange={handleChange}
                fullWidth
                required
                margin="normal"
              />
              <TextField
                label="Focus"
                name="focus"
                value={form.focus}
                onChange={handleChange}
                fullWidth
                margin="normal"
              />
              <Typography variant="subtitle1" sx={{ mt: 2 }}>
                Bullet Points:
              </Typography>
              {form.bulletpoints.map((point, idx) => (
                <Box
                  key={idx}
                  sx={{ display: "flex", alignItems: "center", mb: 1 }}
                >
                  <TextField
                    label={`Point ${idx + 1}`}
                    value={point}
                    onChange={(e) =>
                      handleBulletpointChange(idx, e.target.value)
                    }
                    fullWidth
                    sx={{ mr: 1 }}
                  />
                  <Button
                    color="error"
                    onClick={() => handleRemoveBulletpoint(idx)}
                    disabled={form.bulletpoints.length === 1}
                  >
                    Remove
                  </Button>
                </Box>
              ))}
              <Button onClick={handleAddBulletpoint} sx={{ mt: 1 }}>
                Add Point
              </Button>
            </DialogContent>
            <DialogActions>
              <Button onClick={handleModalClose}>Cancel</Button>
              <Button type="submit" variant="contained">
                {editingId ? "Update" : "Add"}
              </Button>
            </DialogActions>
          </form>
        </Dialog>
        {/* Delete Confirm Dialog */}
        <Dialog open={confirmOpen} onClose={() => setConfirmOpen(false)}>
          <DialogTitle>Confirm Delete</DialogTitle>
          <DialogContent>
            <Typography>Are you sure you want to delete this level?</Typography>
          </DialogContent>
          <DialogActions>
            <Button onClick={() => setConfirmOpen(false)}>Cancel</Button>
            <Button color="error" onClick={handleDelete}>
              Delete
            </Button>
          </DialogActions>
        </Dialog>
        <GlobalToast toast={toast} />
      </Box>
    </>
  );
};

export default AdminLevels;
